import React, { useState } from 'react';
import { FaArrowRight } from "react-icons/fa";
import projectOutsourceImage from '../../assets/project-outsource-image.png';
import facilityMaintenanceBg from '../../assets/facility-maintenance-image.png'
import renovationsBg from '../../assets/renovations-bg.png';
import type {
  ProjectOutsourcingServiceType,
  ProjectOutsourcingUserType,
} from "@laborhack/data/project-outsourcing";

interface Service {
  id: ProjectOutsourcingServiceType;
  title: string;
  description: string;
}

interface ProjectOutsourceProps {
  className?: string;
  onCtaClick: (service: ProjectOutsourcingServiceType) => void;
  userType: ProjectOutsourcingUserType;
}

export const ProjectOutsource: React.FC<ProjectOutsourceProps> = ({
  className = "",
  onCtaClick,
  userType,
}) => {
  const [activeService, setActiveService] =
    useState<ProjectOutsourcingServiceType>("construction");

  const services: Service[] = [
    {
      id: "construction",
      title: "Construction",
      description:
        userType === "homeowner"
          ? "For small to large-scale home projects, hire a skilled workforce to meet your labour needs."
          : "For small to large-scale construction projects, hire a skilled workforce to meet your labour needs.",
    },
    {
      id: "facility-maintenance",
      title: "Facility maintenance",
      description:
        userType === "homeowner"
          ? "Smooth, reliable home maintenance and repairs made simple with our monthly subscription option."
          : "Keep your office or facility running smoothly with expert maintenance and repair services.",
    },
    {
      id: "renovations",
      title: "Renovations",
      description: "Fast, high-quality upgrades for any space.",
    },
  ];

  const getBackgroundImage = () => {
    switch (activeService) {
      case "facility-maintenance":
        return facilityMaintenanceBg;
      case "renovations":
        return renovationsBg;
      case "construction":
      default:
        return projectOutsourceImage;
    }
  };

  return (
    <div className={`w-full bg-[#FEFEFE] py-12 md:py-16 ${className}`}>
      <div className="relative">
        <div className="relative" style={{ border: "0.5px solid #121212" }}>
          {/* Top Section - Main Header */}
          <div className="py-10 md:py-16 px-6 md:px-10">
            <div className="flex flex-col gap-4">
              <h2 className="text-xl md:text-[40px] font-medium text-[#040A0D] leading-[1.2]">
                Outsource your project with peace of mind
              </h2>
              <p className="text-base md:text-xl text-[#464646] leading-[1.6] font-[Inter]">
                {`Need a job done? We'll deliver your project using vetted workers, from start to finish.`}
              </p>
            </div>
          </div>

          {/* Bottom Section with Services and Image */}
          <div
            className="relative grid grid-cols-1 lg:grid-cols-2"
            style={{ borderTop: "0.5px solid #121212" }}
          >
            {/* Left Side - Services List */}
            <div className="relative py-10 px-7 md:px-[45px] overflow-hidden">
              {/* Continuous background border */}
              <div className="absolute left-6 md:left-12 top-10 bottom-10 border-l-[2.5px] border-[#D0D6D9]"></div>

              {/* Services */}
              <div className="max-w-[430px] pb-8">
                {services.map((service, index) => {
                  const isActive = activeService === service.id;
                  return (
                    <div
                      key={service.id}
                      className={`relative flex flex-col gap-2 cursor-pointer transition-colors duration-200 ${
                        index > 0 ? "mt-12 md:mt-16" : ""
                      }`}
                      onClick={() => setActiveService(service.id)}
                      style={{
                        borderLeft: isActive
                          ? "2.5px solid #153243"
                          : "2.5px solid transparent",
                        marginLeft: "-2.5px",
                        paddingLeft: "16px",
                      }}
                    >
                      <h3
                        className={`text-xl md:text-[26px] font-medium leading-[1.23] font-[AeonikPro] ${
                          isActive
                            ? "text-[#212121]"
                            : "text-[#828282] hover:text-[#464646]"
                        }`}
                      >
                        {service.title}
                      </h3>
                      <p
                        className={`text-base md:text-lg leading-[1.67] font-[Inter] ${
                          isActive
                            ? "text-[#464646]"
                            : "text-[#828282] hover:text-[#646464]"
                        }`}
                      >
                        {service.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Side - Box with Image (top) and Button (bottom), centered */}
            <div className="relative flex items-center justify-center p-6">
              <div
                className="p-2 w-full max-w-[480px] flex flex-col gap-4"
                style={{ border: "0.5px solid #121212" }}
              >
                {/* Image Area */}
                <div
                  className="relative w-full aspect-[16/9] bg-cover bg-center transition-all duration-500"
                  style={{
                    backgroundImage: `url(${getBackgroundImage()})`,
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{ backgroundColor: "rgba(49, 28, 28, 0.4)" }}
                  />
                </div>

                {/* Button */}
                <button
                  onClick={() =>
                    onCtaClick(
                      activeService as
                        | "construction"
                        | "facility-maintenance"
                        | "renovations"
                    )
                  }
                  className="w-full bg-[#ED6E5C] hover:bg-[#ED6E5C]/80 text-[#FEFEFE] transition-colors duration-200 flex items-center justify-center gap-2 font-[Inter] text-lg font-medium leading-[1.333]"
                  style={{ height: "60px" }}
                >
                  Start project
                  <FaArrowRight className="w-3 lg:w-4 h-3 lg:h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
